import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CustomizableSelect } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Toggle } from "@/components/Toggle";
import useAIConfigs from "@/hooks/useAIConfigs";
import useTeams from "@/hooks/useTeams";
import useApi from "@/hooks/useApi";
import { AIConfiguration } from "../types";
import { User } from "../../Users/types";
import AINewSettingsTabs from "./AINewSettingsTabs";

export default function AINewSettingsHandoff() {
  const { id } = useParams();
  const { aiConfigurations, loading, update } = useAIConfigs();
  const { teams } = useTeams();
  const api = useApi();

  const [config, setConfig] = useState<AIConfiguration>();
  const [configIndex, setConfigIndex] = useState<number>();
  const [users, setUsers] = useState<User[]>([]);
  const [textAreaFocused, setTextAreaFocused] = useState(false);

  useEffect(() => {
    if (aiConfigurations.length > 0 && id) {
      const index = aiConfigurations.findIndex((c) => c.id === parseInt(id));
      if (index !== -1) {
        setConfig(aiConfigurations[index]);
        setConfigIndex(index);
      }
    }
  }, [aiConfigurations, id]);

  useEffect(() => {
    async function fetchAgents() {
      try {
        const response = await api.messaging.get("Users/List/");
        if (response.ok) {
          setUsers(await response.json());
        }
      } catch (error) {
        console.error("Failed to fetch agents:", error);
      }
    }
    fetchAgents();
  }, [api.messaging]);

  const handleUpdate = (field: keyof AIConfiguration, value: any) => {
    if (!config || configIndex === undefined) return;
    const updated = { ...config, [field]: value };
    setConfig(updated);
    update(updated, configIndex);
  };

  const handleEscalationRulesChange = (value: string) => {
    if (value.length >= 10000) return;
    handleUpdate("escalationRules", value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const currentRules = config?.escalationRules || "";
      const lines = currentRules.split("\n").filter(l => l.trim());
      const nextNumber = lines.length + 1;
      handleUpdate("escalationRules", currentRules + `\n${nextNumber}. `);
    }
  };

  const defaultEscalationRules =
    "1. I want to talk to a human\n2. Transfer me to an agent\n3. This isn't helping\n4. I need a manager";

  const handleTextAreaFocus = () => {
    if (!textAreaFocused && !config?.escalationRules) {
      setTextAreaFocused(true);
      handleUpdate("escalationRules", defaultEscalationRules);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <AINewSettingsTabs aiConfigId={id!} />

      <div className="bg-white p-6 border rounded-2xl shadow-sm w-[750px] self-center mt-4">
        {/* Header */}
        <div className="border-b pb-4 mb-4">
          <div className="font-semibold text-lg">Agent Handoff</div>
          <div className="text-slate-500 text-sm">
            Choose how the chatbot will behave when it can't answer a question or when a visitor asks for agent help
          </div>
        </div>

        {/* Conversation Escalation Toggle */}
        <div className="py-3 flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="font-semibold text-sm pb-1">Conversation Escalation</div>
            <div className="text-slate-500 text-sm">
              Enable the option for visitors to escalate to a human agent when the chatbot can't answer a question.
            </div>
          </div>
          <Toggle
            toggled={config.conversationEscalation || false}
            changeToggle={() => handleUpdate("conversationEscalation", !config.conversationEscalation)}
          />
        </div>

        {/* Escalation Phrases */}
        {config.conversationEscalation && (
          <div className="py-3">
            <div className="text-sm pb-2">
              Add phrases to train the chatbot for agent escalation requests. More phrases improves accuracy.
            </div>
            <textarea
              value={config.escalationRules || ""}
              onChange={(e) => handleEscalationRulesChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleTextAreaFocus}
              placeholder="1. Type a sentence"
              rows={5}
              className="w-full p-2 border rounded-md text-sm"
            />
          </div>
        )}

        {/* Assignment Type */}
        <div className={`py-3 ${!config.conversationEscalation ? "opacity-50" : ""}`}>
          <div className="font-semibold text-sm pb-1">Assignment</div>
          <div className="text-slate-500 text-sm pb-2">
            Choose how to route escalated conversations
          </div>
          <div className={!config.conversationEscalation ? "pointer-events-none" : ""}>
            <CustomizableSelect
              options={[
                { label: "Team", action: () => handleUpdate("escalationMode", "team") },
                { label: "Agent", action: () => handleUpdate("escalationMode", "agent") }
              ]}
              defaultOption={{
                label: config.escalationMode === "agent" ? "Agent" : "Team",
                action: () => {}
              }}
            />
          </div>
        </div>

        {/* Team Selection */}
        {config.escalationMode !== "agent" && teams.length > 0 && (
          <div className={`py-3 ${!config.conversationEscalation ? "opacity-50 pointer-events-none" : ""}`}>
            <div className="font-semibold text-sm pb-1">Select Team</div>
            <CustomizableSelect
              options={teams.map((team) => ({
                label: team.name,
                action: () => handleUpdate("escalationTeamId", team.id)
              }))}
              defaultOption={{
                label: teams.find(t => t.id === config.escalationTeamId)?.name || "Select a team",
                action: () => {}
              }}
            />
          </div>
        )}

        {/* Agent Selection */}
        {config.escalationMode === "agent" && users.length > 0 && (
          <div className={`py-3 ${!config.conversationEscalation ? "opacity-50 pointer-events-none" : ""}`}>
            <div className="font-semibold text-sm pb-1">Select Agent</div>
            <CustomizableSelect
              options={users.map((user) => ({
                label: user.displayName,
                action: () => handleUpdate("escalationAgentId", user.id)
              }))}
              defaultOption={{
                label: users.find(u => u.id === config.escalationAgentId)?.displayName || "Select an agent",
                action: () => {}
              }}
            />
          </div>
        )}

        {config.escalationMode !== "agent" && teams.length === 0 && config.conversationEscalation && (
          <div className="py-3 text-sm text-slate-500">
            No teams available. Create teams in General Settings → Teams.
          </div>
        )}

        {config.escalationMode === "agent" && users.length === 0 && config.conversationEscalation && (
          <div className="py-3 text-sm text-slate-500">
            No agents available. Add users in General Settings → Users.
          </div>
        )}
      </div>
    </div>
  );
}
